import { GENDER_OPTIONS } from "constants";
import { PhoneNumberUtil } from "google-libphonenumber";
import moment from "moment";
import { deserializePhoneNumber, serializePhoneNumber } from "utils/common";
import z from "zod";
import {
  addressFromJson,
  addressSchemaToJson,
  resolveDefaultAddressJson,
} from "./address";
import { membershipFromJson } from "./membership";

export const patientFirstNameSchemaField = z
  .string()
  .trim()
  .min(1, { message: "First Name is required" });
export const patientLastNameSchemaField = z
  .string()
  .trim()
  .min(1, { message: "Last Name is required" });
export const patientPhoneNumberSchemaField = z
  .string()
  .min(1, { message: "Phone Number is required" })
  .refine(
    (phone) => {
      try {
        const phoneUtil = PhoneNumberUtil.getInstance();
        let normalizedPhone = "+1" + phone.replace(/_|\s|-|\)|\(/g, "");
        let result = phoneUtil.isValidNumber(
          phoneUtil.parseAndKeepRawInput(normalizedPhone),
        );
        return result;
      } catch {
        return false;
      }
    },
    { message: "Incorrect Phone Number" },
  );
export const patientBirthdaySchemaField = z.custom(
  (val) => val && moment.isMoment(val),
  {
    message: "Birthday is required",
  },
);
export const patientEmailSchemaField = z.email({ message: "Invalid email" });
export const patientSexSchemaField = z.enum(
  GENDER_OPTIONS.map((option) => option.value),
  {
    message: "Gender is required",
  },
);
export const patientSuiteSchemaField = z
  .string()
  .trim()
  .max(12, { message: "Suite must be less than 12 characters" })
  .nullish();

export const patientAddressSchemaField = z
  .looseObject({})
  .nullable()
  .refine((val) => val, { message: "Address is required" });

export const patientMembershipsSchemaField = z
  .array(z.looseObject({}))
  .optional()
  .nullable();
export const patientSchemaToJson = (value) => {
  return {
    first_name: value.firstName,
    last_name: value.lastName,
    phone_number: serializePhoneNumber(value.phoneNumber),
    date_of_birth: value.birthday.format("YYYY-MM-DD"),
    email: value.email,
    sex: value.sex,
    default_address: {
      ...(value.address ? addressSchemaToJson(value.address) : {}),
      suite: value.suite || undefined,
    },
    memberships: value.memberships?.map((membership) => membership.uuid),
  };
};

export const patientFromJson = (json) => {
  // TODO(anyone): remove || json.firstName, json.lastName, ... once patientFromJson won't be used to parse currentUser
  const defaultAddress = resolveDefaultAddressJson(json);

  return {
    uuid: json.uuid,
    firstName: json.first_name || json.firstName,
    lastName: json.last_name || json.lastName,
    fullName: `${json.first_name || json.firstName} ${json.last_name || json.lastName}`,
    phoneNumber: json.phone || deserializePhoneNumber(json.phone_number),
    birthday: moment(json.date_of_birth || json.birthday),
    email: json.email,
    sex: json.sex,
    suite: defaultAddress?.suite || defaultAddress?.apartment,
    address: defaultAddress ? addressFromJson(defaultAddress) : null,
    memberships: json.memberships?.map(membershipFromJson),
  };
};
